import * as React from 'react';
import { RouteComponentProps } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import authService from './api-authorization/AuthorizeService';
import DriverTabData from './DriverTab';
import DriverJobsListData from './DriverJobsList';
import DriverJobsDepartOfficeData from './DriverJobsDepartOffice';
import DriverJobsSiteArrivalData from './DriverJobsSiteArrival';
import DriverJobsSiteArrivalSwmsData from './DriverJobsSiteArrivalSwms';
import DriverJobsSiteDepartureData from './DriverJobsSiteDeparture';
import DriverJobsSiteDepartureSkipData from './DriverJobsSiteDepartureSkip';
import DriverJobsSiteDeparturePartData from './DriverJobsSiteDeparturePart';
import DriverJobsSiteDeparturePartItemsData from './DriverJobsSiteDeparturePartItems';
import DriverJobsSiteDeparturePartQuestionsData from './DriverJobsSiteDeparturePartQuestions';
import DriverJobsSiteDepartureVoidQuestionsData from './DriverJobsSiteDepartureVoidQuestions';
import DriverJobsSiteDepartureCompleteData from './DriverJobsSiteDepartureComplete';
import DriverJobsSiteDepartureCompleteQuestionsData from './DriverJobsSiteDepartureCompleteQuestions';
import { VoidGearList, VoidQuestions, VoidSizeQuantity } from './models/JobScaffoldInterfaces';
import * as UtilsGoogleMaps from './UtilsGoogleMaps';
import { GoogleAddressItem } from './models/JobSharedInterfaces';

interface DriverJobs {
    loading: boolean;
    truckOptions: GenericList[];
    truckJobList: TruckJobList[];
    scheduleTypes: GenericList[];
    truckNotes: string;
    showDepartOffice: boolean;
    showArriveSite: boolean;
    showArriveSiteSwms: boolean;
    showDepartSite: boolean;
    showSkipJob: boolean;
    skipJobNotes: string;
    showPartComplete: boolean;
    showPartItems: boolean;
    showPartQuestions: boolean;
    partCompleteJob: PartCompleteJob;
    isPickup: boolean;  //only true if all items on the job are pickups
    showComplete: boolean;
    showCompleteQuestions: boolean;
    showVoidQuestions: boolean;
    completeJob: PlanChanges;
    completionCheck: CompletionCheck;
    jobType: number;
    isKwikstage: boolean;
    siteAddress: string;
    team: GenericList[];
    truckIndex: number;
    jobIndex: number;
    isAssistJob: boolean;
    isVoidJob: boolean;
    isManualJob: boolean;
    isDriverJob: boolean;   //means the driver added so can move
    arrivalCheck: ArrivalCheck;
    scheduleIds: number[];
    departureCheck: DepartureCheck;
    voidGearList: VoidGearList[];
    voidQuestionsBlank: VoidQuestions;  //used for resetting void questions
    swmsOptions: GenericList[];     //dropdown with swms id and item name eg. 1 Handrail
    swmsManualItems: SwmsItem[];
    saveDisabled: boolean;
}

interface TruckJobList {
    truckId: number;
    estimatedSiteTimeFormatted: string;
    actualSiteTimeFormatted: string;
    estimatedTravelTimeFormatted: string;
    actualTravelTimeFormatted: string;
    truckCheckComplete: boolean;
    truckToolCheckComplete: boolean;
    truckFinalToolCheckComplete: boolean;
    allJobsComplete: boolean;
    hasDepartedOffice: boolean;
    hasArrivedOffice: boolean;
    hadLunch: boolean;
    truckNotes: string;
    jobs: DriverJob[];
}

interface DriverJob {
    id: number;
    jobId: number;
    truckId: number;
    jobType: number;    //1 = Install, 2 = Pickup, 3 Both Install and Pickup Items
    isKwikstage: boolean;   //One or more of the items is Kwikstage
    officeAddress: string;
    officeAddressLatitude: number;
    officeAddressLongitude: number;
    siteAddress: string;
    siteAddressGoogle: string;
    siteAddressLatitude: number;
    siteAddressLongitude: number;
    siteContact: string;
    customer: string;
    gateCode: string;
    team: GenericList[];
    estimatedTime: string;
    jobNotes: string[];
    items: DriverJobItems[];
    hasArrived: boolean;
    isComplete: boolean;
    truckAccess: boolean;
    parking: boolean;
    siteClean: boolean;
    powerlinesWithinRange: boolean;
    siteHasIssues: boolean;
    showSiteIssues: boolean;
    isAssistJob: boolean;
    isManualJob: boolean;
    isDriverJob: boolean;   //means the driver added so can move
    vehicleItemOrder: number;
    plans: PlanFile[];
    voidGearList: VoidGearList[];   //used for Void Items Only - arrays will be empty for items that aren't blank
}

interface DriverJobItems {
    id: number;
    kwikstageId: number;
    unitNumberDisplay: string;
    type: string;
    typeId: number;
    item: string;
    itemId: number;
    isVoid: boolean;
    quantity: number;
    itemQuantityType: string;
    estimatedTime: string;
    estimatedTimeMins: number;
    isComplete: boolean;
    percentComplete: number;    //used for Kwikstage
    vehicleItemOrder: number;   //used when adding manual job after a linked job
    itemParts: ItemPart[];
    other: boolean;
    otherDisabled: boolean;     //disabled if one or more item parts are ticked
}
interface ItemPart {
    itemPartId: number;
    part: string;
    isComplete: boolean;
}

interface DepartureCheck {
    id: number;
    gearCheck: number;
    teamMemberPpe: number;
}

interface ArrivalCheck {
    id: number;
    hazardsRemoved: number;
    workAreasChecked: number;
    layoutPlanMaterialsAvailable: number;
    siteDamage: number;
    siteDamageNotes: string;
    siteSwmsChecked: number;
}

interface PartCompleteJob {
    planChanges: PlanChanges;
    jobItems: DriverJobItems[];
    notes: string;
}

interface PlanChanges {
    planChanges: number;
    plan: File,
    planComments: string;
    notes: string;
}

interface CompletionCheck {
    swmsSiteContact: number;
    scaffoldSquare: number;
    scaffoldGaps: number;
    accessPointsInstalled: number;
    scaffoldSupported: number;
    safetySigns: number;
    handrailsChecked: number;
    gapsChecked: number;
    fittingsSecure: number;
    siteChecked: number;
    rubbishInBin: number;
    siteSecured: number;
    scaffoldSafeCondition: number;
    scaffoldDamage: number;
    missingEquipment: number;
    scaffoldCertificateSigned: number;
    scaffoldTicketPhoto: number;
    scaffoldPhotosComplete: number;
    scaffoldTicketSignedById: number;
    notes: string;
}

interface GenericList {
    id: number;
    name: string;
}

interface PlanFile {
    id: number;
    fileName: string;
}

interface SwmsItem {
    swmsId: number;
    hazard: string;
    control: string;
}

export class DriverJobsData extends React.Component<RouteComponentProps<{}>, DriverJobs> {
    constructor(props: any) {
        super(props);

        this.state = {
            loading: true,
            truckOptions: [],
            truckJobList: [],
            scheduleTypes: [],
            truckNotes: "",
            showDepartOffice: false,
            showArriveSite: false,
            showArriveSiteSwms: false,
            showDepartSite: false,
            showSkipJob: false,
            skipJobNotes: "",
            showPartComplete: false,
            showPartItems: false,
            showPartQuestions: false,
            isPickup: false,
            showComplete: false,
            showCompleteQuestions: false,
            showVoidQuestions: false,
            partCompleteJob: {
                planChanges: {
                    planChanges: -1,
                    plan: new File([""], "filename"),
                    planComments: "",
                    notes: ""
                },
                jobItems: [],
                notes: ""
            },
            completeJob: {
                planChanges: -1,
                plan: new File([""], "filename"),
                planComments: "",
                notes: ""
            },
            completionCheck: {
                swmsSiteContact: -1,
                scaffoldSquare: -1,
                scaffoldGaps: -1,
                accessPointsInstalled: -1,
                scaffoldSupported: -1,
                safetySigns: -1,
                handrailsChecked: -1,
                gapsChecked: -1,
                fittingsSecure: -1,
                siteChecked: -1,
                rubbishInBin: -1,
                siteSecured: -1,
                scaffoldSafeCondition: -1,
                scaffoldDamage: -1,
                missingEquipment: -1,
                scaffoldCertificateSigned: -1,
                scaffoldTicketPhoto: -1,
                scaffoldPhotosComplete: -1,
                scaffoldTicketSignedById: -1,
                notes: ""
            },
            jobType: 0,
            isKwikstage: false,
            siteAddress: "",
            team: [],
            truckIndex: -1,
            jobIndex: -1,
            isAssistJob: false,
            isVoidJob: false,
            isManualJob: false,
            isDriverJob: false,
            departureCheck: {
                id: 0,
                gearCheck: -1,
                teamMemberPpe: -1
            },
            arrivalCheck: {
                id: 0,  
                hazardsRemoved: -1,
                workAreasChecked: -1,
                layoutPlanMaterialsAvailable: -1,
                siteDamage: -1,
                siteDamageNotes: "",
                siteSwmsChecked: -1
            },
            voidGearList: [],
            voidQuestionsBlank: {
                braces: [],
                floorEnds: [],
                plateEnds: [],
                hookEnds: [],
                ladders: [],
                noLadder: false,
                noLadderReason: "",
                ladderExtras: [],
                accessPanels: [],
                noAccessPanels: false,
                noAccessPanelsReason: "",
                decks700: [],
                decks550: [],
                decks300: [],
                decks150: [],
                noDecks: false,
                noDecksReason: ""
            },
            scheduleIds: [],
            swmsOptions: [],
            swmsManualItems: [],
            saveDisabled: false
        };
    }

    componentDidMount() {
        window.scrollTo(0, 0);

        this.getData();
    }

    async getData() {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        axios.get('api/Job/GetDriverJobs?SubId=' + user.sub, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            this.setState({
                truckOptions: res.data.truckOptions, truckIndex: res.data.truckIndex,
                truckJobList: res.data.truckJobList, scheduleTypes: res.data.scheduleTypes, truckNotes: res.data.truckNotes,
                voidQuestionsBlank: res.data.voidQuestions, loading: false
            });
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    refreshJobs = () => {
        this.getData();
    }

    updateJobs = (updatedJobs: DriverJob[]) => {
        var truckJobs = this.state.truckJobList;
        var truckJob = truckJobs[this.state.truckIndex];
        if (truckJob) {
            truckJob.jobs = updatedJobs;
            this.setState({ truckJobList: truckJobs });
        }
    }

    openTomorrowJobs = (e: any) => {
        e.preventDefault();

        this.setState({
            showDepartOffice: false,
            showArriveSite: false,
            showDepartSite: false,
            showSkipJob: false,
            showPartComplete: false,
            showPartItems: false,
            showPartQuestions: false,
            showComplete: false,
            showCompleteQuestions: false
        });
    }

    closeTomorrowJobs = () => {
        this.setState({
            showDepartOffice: false,
            showArriveSite: false,
            showDepartSite: false,
            showSkipJob: false,
            showPartComplete: false,
            showPartItems: false,
            showPartQuestions: false,
            showComplete: false,
            showCompleteQuestions: false
        });
    }

    showSiteIssues = (jobIndex: number) => {
        var truckJobList = this.state.truckJobList;
        var jobs = truckJobList[this.state.truckIndex].jobs;
        var job = jobs[jobIndex];

        job.showSiteIssues = !job.showSiteIssues;

        this.setState({ truckJobList: truckJobList });
    }

    async getActualTimes(truckId: number) {
        const token = await authService.getAccessToken();
        var truckJobList = this.state.truckJobList;
        var truckJob = truckJobList[this.state.truckIndex];

        axios.get('api/Job/GetDriverActualTimes?VehicleId=' + truckId, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            truckJob.actualSiteTimeFormatted = res.data.actualSiteTimeFormatted;
            truckJob.actualTravelTimeFormatted = res.data.actualTravelTimeFormatted;
            this.setState({ truckJobList: truckJobList });
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    //update the truck selected
    updateTruckIndex = (truckIndex: number) => {
        this.setState({ truckIndex: truckIndex });
    }

    //depart office
    departOffice = () => {
        this.setState({ showDepartOffice: true });
    }

    resetDepartureCheck = () => {
        var departureCheck: DepartureCheck = {
            id: 0,
            gearCheck: -1,
            teamMemberPpe: -1
        };

        return departureCheck;
    }

    updateDepartureCheck = (departureCheck: DepartureCheck) => {
        this.setState({ departureCheck: departureCheck });
    }

    saveDepartOffice = () => {
        if (this.state.saveDisabled) {
            return;
        }
        this.setState({ saveDisabled: true });
        this.departOfficeSave();
    }

    async departOfficeSave() {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        var truckJobs = this.state.truckJobList;
        var truckJob = truckJobs[this.state.truckIndex];
        var jobs = truckJob.jobs;
        if (jobs.length > 0) {
            var departureCheck = this.state.departureCheck;

            //set departure time for all scheduled items for first job
            var job = jobs[0];
            var officeAddrLatLng: GoogleAddressItem = {
                lat: job.officeAddressLatitude,
                lng: job.officeAddressLongitude,
                fullAddress: job.officeAddress
            };

            var scheduleIds: number[] = [];
            for (var i = 0; i < job.items.length; i++) {
                scheduleIds.push(job.items[i].id);
            }

            //calculate distance to first job via google
            var duration = 0;
            var jobAddrLatLng: GoogleAddressItem = {
                lat: job.siteAddressLatitude,
                lng: job.siteAddressLongitude,
                fullAddress: job.siteAddress
            };
            var result = await UtilsGoogleMaps.calculateDistance(officeAddrLatLng, jobAddrLatLng);
            if (result) {
                duration = result.duration;
            };
            var estTravelTimeMins = Math.round(duration / 60);

            //calcuate estimated travel time route
            var siteAddresses: GoogleAddressItem[] = [];
            for (var j = 0; j < jobs.length; j++) {
                var siteAddrLatLng: GoogleAddressItem = {
                    lat: jobs[j].siteAddressLatitude,
                    lng: jobs[j].siteAddressLongitude,
                    fullAddress: jobs[j].siteAddress
                };
                siteAddresses.push(siteAddrLatLng);
            }
            var totalDuration = 0;
            var result = await UtilsGoogleMaps.calculateDistanceRoute(officeAddrLatLng, siteAddresses);
            if (result) {
                totalDuration = result.duration;
            };

            if (totalDuration === 0) {
                //remove manual jobs and try again
                var siteAddressExclManual: GoogleAddressItem[] = [];
                for (var j = 0; j < jobs.length; j++) {
                    if (!jobs[j].isManualJob) {
                        var siteAddrExManualLatLng: GoogleAddressItem = {
                            lat: jobs[j].siteAddressLatitude,
                            lng: jobs[j].siteAddressLongitude,
                            fullAddress: jobs[j].siteAddress
                        };
                        siteAddressExclManual.push(siteAddrExManualLatLng);
                    }
                }

                totalDuration = 0;
                if (siteAddressExclManual.length > 0) {

                    var result = await UtilsGoogleMaps.calculateDistanceRoute(officeAddrLatLng, siteAddressExclManual);
                    if (result) {
                        totalDuration = result.duration;
                    };
                }
            }
            var estRouteTravelTimeMins = Math.round(totalDuration / 60);

            var departOffice = {
                scheduleIds: scheduleIds,
                vehicleId: truckJob.truckId,
                teamMemberPpe: departureCheck.teamMemberPpe,
                estTravelTimeMins: estTravelTimeMins,
                estRouteTravelTimeMins: estRouteTravelTimeMins,
                subId: user.sub,
            };

            axios.post('api/Job/DepartOffice', departOffice, {
                headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
            })
            .then(res => {
                if (res.data.isError) {
                    var errors = res.data.messages as any[];
                    errors.map(function (error: any) {
                        toast.error(error.content);
                    });
                } else {
                    toast.success("Depart Office has been saved");
                    var departureChecks = this.resetDepartureCheck();

                    truckJob.hasDepartedOffice = true;
                    truckJob.hasDepartedOffice = res.data.runsheetTime;
                    truckJob.estimatedTravelTimeFormatted = res.data.estTravelTime;

                    this.setState({ showDepartOffice: false, departureCheck: departureChecks, truckJobList: truckJobs, saveDisabled: false });
                }
            })
            .catch(error => {
                toast.error(error.message);
            });
        }
  
    }

    //Arrive Office
    arriveOffice = () => {
        this.arriveOfficeSave();
    }

    async arriveOfficeSave() {
        const token = await authService.getAccessToken();

        var truckJobList = this.state.truckJobList;
        var truckJob = truckJobList[this.state.truckIndex];

        var arriveOffice = {
            vehicleId: truckJob.truckId
        };

        axios.post('api/Job/ArriveOffice', arriveOffice, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            if (res.data.isError) {
                var errors = res.data.messages as any[];
                errors.map(function (error: any) {
                    toast.error(error.content);
                });
            } else {
                toast.success("Arrive Office has been saved");
                truckJob.hasArrivedOffice = true;

                this.setState({ truckJobList: truckJobList, saveDisabled: false });

                //re-calculate the overall actual travel time
                this.getActualTimes(truckJob.truckId);
            }
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    //ARRIVAL
    startJob = (jobIndex: number) => {
        this.getSwmsOptions(jobIndex);
    }

    async getSwmsOptions(jobIndex: number) {
        const token = await authService.getAccessToken();

        var jobs = this.state.truckJobList[this.state.truckIndex].jobs;
        var job = jobs[jobIndex];
        var items = job.items;

        var scheduleIds: number[] = [];
        scheduleIds.push(job.id);
        for (var i = 0; i < items.length; i++) {
            scheduleIds.push(items[i].id);
        }

        var getSwms = {
            scheduleIds: scheduleIds
        };

        axios.post('api/Job/GetSwmsOptions', getSwms, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            var arrivalCheck = this.resetArrivalCheck();
            this.setState({ swmsOptions: res.data, showArriveSite: true, jobIndex: jobIndex, arrivalCheck: arrivalCheck });
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    resetArrivalCheck = () => {
        var arrivalCheck: ArrivalCheck = {
            id: 0,
            hazardsRemoved: -1,
            workAreasChecked: -1,
            layoutPlanMaterialsAvailable: -1,
            siteDamage: -1,
            siteDamageNotes: "",
            siteSwmsChecked: -1
        };

        return arrivalCheck;
    }

    updateManualSwms = (manualSwms: SwmsItem[]) => {
        this.setState({ swmsManualItems: manualSwms });
    }

    backtoJobs = () => {
        var arrivalCheck = this.resetArrivalCheck();
        this.setState({ swmsOptions: [], swmsManualItems: [], scheduleIds: [], showArriveSite: false, showArriveSiteSwms: false, jobIndex: - 1, arrivalCheck: arrivalCheck });
    }

    updateArrivalCheck = (arrivalCheck: ArrivalCheck) => {
        this.setState({ arrivalCheck: arrivalCheck });
    }

    //swms
    gotoSwms = () => {
        var jobs = this.state.truckJobList[this.state.truckIndex].jobs;
        var job = jobs[this.state.jobIndex];
        var items = job.items;

        var scheduleIds: number[] = [];
        scheduleIds.push(job.id);
        for (var i = 0; i < items.length; i++) {
            scheduleIds.push(items[i].id);
        }

        this.setState({ scheduleIds: scheduleIds, showArriveSite: false, showArriveSiteSwms: true });
    }

    backtoArrivalChecks = () => {
        this.setState({ showArriveSite: true, showArriveSiteSwms: false });
    }

    updateSwmsChecked = (swmsChecked: number) => {
        let arrival = this.state.arrivalCheck;
        arrival.siteSwmsChecked = swmsChecked;
        this.setState({ arrivalCheck: arrival });
    }

    saveArriveSite = () => {
        if (this.state.saveDisabled) {
            return;
        }
        this.setState({ saveDisabled: true });
        this.arriveSite();
    }

    async arriveSite() {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        var truckJobList = this.state.truckJobList;
        var truckJob = truckJobList[this.state.truckIndex];
        var jobs = truckJob.jobs;
        var job = jobs[this.state.jobIndex];
        var items = job.items;

        var scheduleIds: number[] = [];
        for (var i = 0; i < items.length; i++) {
            scheduleIds.push(items[i].id);
        }
        //get previous job (if there is one) so we can calculate actual travel time
        var prevScheduleId = this.state.jobIndex === 0 ? 0 : jobs[this.state.jobIndex - 1].id;

        var arrivalCheck = this.state.arrivalCheck;
        var swmsChecked = arrivalCheck.siteSwmsChecked < 0 ? 0 : arrivalCheck.siteSwmsChecked;  //not all items have swms

        var arriveOnSite = {
            scheduleIds: scheduleIds,
            prevScheduleId: prevScheduleId,     //used for calculating actual travel time
            vehicleId: truckJob.truckId,
            jobId: job.jobId,
            siteAddressManual: job.siteAddress,
            hazardsRemoved: arrivalCheck.hazardsRemoved,
            workAreasChecked: arrivalCheck.workAreasChecked,
            layoutPlanMaterialsAvailable: arrivalCheck.layoutPlanMaterialsAvailable,
            siteDamage: arrivalCheck.siteDamage,
            siteDamageNotes: arrivalCheck.siteDamageNotes,
            siteSwmsChecked: swmsChecked,
            swmsManualItems: this.state.swmsManualItems,
            subId: user.sub,
        };

        axios.post('api/Job/ArriveSite', arriveOnSite, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            if (res.data.isError) {
                var errors = res.data.messages as any[];
                errors.map(function (error: any) {
                    toast.error(error.content);
                });
            } else {
                toast.success("Arrival has been saved");
                //update job to arrived
                job.hasArrived = true;

                var resetArrivalCheck = this.resetArrivalCheck();

                this.setState({ swmsOptions: [], swmsManualItems: [], showArriveSite: false, showArriveSiteSwms: false, scheduleIds: [], truckJobList: truckJobList, jobIndex: -1, arrivalCheck: resetArrivalCheck, saveDisabled: false });

                //re-calculate the overall actual travel time
                this.getActualTimes(truckJob.truckId);
            }
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    //DEPART SITE
    departSite = (jobIndex: number) => {
        var truckJobList = this.state.truckJobList;
        var truckJob = truckJobList[this.state.truckIndex];
        var jobs = truckJob.jobs;
        var job = jobs[jobIndex];
        var isVoid = job.items.filter(i => i.isVoid).length > 0;

        this.setState({ showDepartSite: true, jobIndex: jobIndex, isAssistJob: job.isAssistJob, isVoidJob: isVoid, isManualJob: job.isManualJob });
    }

    cancelDepartSite = () => {
        this.setState({ showDepartSite: false, jobIndex: -1, isAssistJob: false, isVoidJob: false, isManualJob: false });
    }

    //Skip Job
    skipJob = () => {
        this.setState({ showDepartSite: false, showSkipJob: true, showPartComplete: false, showPartQuestions: false, showComplete: false, showCompleteQuestions: false });
    }

    skipJobBack = () => {
        this.setState({ showDepartSite: true, showSkipJob: false, showPartComplete: false, showPartQuestions: false, showComplete: false, showCompleteQuestions: false });
    }

    updateSkipJobNotes = (notes: string) => {
        this.setState({ skipJobNotes: notes });
    }

    saveSkipJob = () => {
        if (this.state.saveDisabled) {
            return;
        }
        this.setState({ saveDisabled: true });
        this.skipJobSave();
    }

    async skipJobSave() {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        var truckJobList = this.state.truckJobList;
        var truckJob = truckJobList[this.state.truckIndex];
        var jobs = truckJob.jobs;
        var jobIndex = this.state.jobIndex;
        var job = jobs[jobIndex];
        var items = job.items;

        var siteAddrLatLng: GoogleAddressItem = {
            lat: job.siteAddressLatitude,
            lng: job.siteAddressLongitude,
            fullAddress: job.siteAddress
        };

        var scheduleIds: number[] = [];
        for (var i = 0; i < items.length; i++) {
            scheduleIds.push(items[i].id);
        }

        //check if there is another job after this one as need to update departure time
        var duration = 0;
        var nextscheduleIds: number[] = [];
        var nextIndex = jobIndex + 1;
        if (nextIndex < jobs.length) {
            var nextJob = jobs[nextIndex];
            if (nextJob) {
                var nextJobItems = nextJob.items;
                for (var i = 0; i < nextJobItems.length; i++) {
                    nextscheduleIds.push(nextJobItems[i].id);
                }

                var nextJobAddrLatLng: GoogleAddressItem = {
                    lat: nextJob.siteAddressLatitude,
                    lng: nextJob.siteAddressLongitude,
                    fullAddress: nextJob.siteAddress
                };

                //calculate distance to job via google
                var result = await UtilsGoogleMaps.calculateDistance(siteAddrLatLng, nextJobAddrLatLng);
                if (result) {
                    duration = result.duration;
                };
            }
        }

        var estTravelTimeMins = Math.round(duration / 60);
        var truck = this.getTruckName(truckJob.truckId);

        var skipJob = {
            scheduleIds: scheduleIds,
            nextScheduleIds: nextscheduleIds,
            notes: this.state.skipJobNotes,
            siteAddress: job.siteAddress,
            truck: truck,
            estTravelTimeMins: estTravelTimeMins,
            jobId: job.jobId,
            subId: user.sub,
        };

        axios.post('api/Job/SkipJob', skipJob, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            if (res.data.isError) {
                var errors = res.data.messages as any[];
                errors.map(function (error: any) {
                    toast.error(error.content);
                });
            } else {
                toast.success("Job has been saved");
                //update job to arrived
                job.isComplete = true;
                var allJobsComplete = false;
                if (nextscheduleIds.length === 0) {
                    allJobsComplete = true;
                }
                truckJob.allJobsComplete = allJobsComplete;

                this.setState({ truckJobList: truckJobList, showSkipJob: false, showPartComplete: false, showPartQuestions: false, showComplete: false, showCompleteQuestions: false, jobIndex: -1, skipJobNotes: "", saveDisabled: false, isAssistJob: false, isVoidJob: false, isManualJob: false });

                //re-calculate the overall actual site time
                this.getActualTimes(truckJob.truckId);
            }
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    getTruckName = (truckId: number) => {
        var truck = "";
        var truckOption = this.state.truckOptions.find(t => t.id === truckId);
        if (truckOption) {
            truck = truckOption.name;
        }
        return truck;
    }

    //Part Complete Job
    partCompleteJob = () => {
        var partCompleteJob = this.resetPartComplete();
        var completeQuestons = this.resetCompletionCheck();

        var truckJobList = this.state.truckJobList;
        var truckJob = truckJobList[this.state.truckIndex];
        var jobs = truckJob.jobs;
        var job = jobs[this.state.jobIndex];
        partCompleteJob.jobItems = job.items;

        this.setState({ showDepartSite: false, showPartComplete: true, showPartQuestions: false, showComplete: false, showCompleteQuestions: false, partCompleteJob: partCompleteJob, completionCheck: completeQuestons, jobType: job.jobType, isKwikstage: job.isKwikstage, siteAddress: job.siteAddress, team: job.team });
    }

    partCompleteJobBack = () => {
        var partCompleteJob = this.resetPartComplete();
        var completeQuestons = this.resetCompletionCheck();
        this.setState({ showDepartSite: true, showPartComplete: false, showPartQuestions: false, showComplete: false, showCompleteQuestions: false, partCompleteJob: partCompleteJob, completionCheck: completeQuestons, jobType: 0, isKwikstage: false, siteAddress: "", team: [] });
    }

    resetPartComplete = () => {
        var partCompleteJob: PartCompleteJob = {
            planChanges: {
                planChanges: -1,
                plan: new File([""], "filename"),
                planComments: "",
                notes: ""
            },
            jobItems: [],
            notes: ""
        };

        return partCompleteJob;
    }

    updatePartComplete = (partCompleteJob: PartCompleteJob) => {
        this.setState({ partCompleteJob: partCompleteJob });
    }

    goToPartItems = () => {
        this.setState({ showPartComplete: false, showPartItems: true });
    }

    partItemsBack = () => {
        this.setState({ showPartComplete: true, showPartItems: false });
    }

    gotoPartQuestions = () => {
        this.setState({ showPartItems: false, showPartQuestions: true });
    }

    partQuestionsBack = () => {
        this.setState({ showPartItems: true, showPartQuestions: false });
    }

    updatePartQuestions = (completionCheck: CompletionCheck) => {
        this.setState({ completionCheck: completionCheck });
    }

    savePartComplete = () => {
        if (this.state.saveDisabled) {
            return;
        }
        this.setState({ saveDisabled: true });
        this.partCompleteSave();
    }

    async partCompleteSave() {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        var truckJobList = this.state.truckJobList;
        var truckJob = truckJobList[this.state.truckIndex];
        var jobs = truckJob.jobs;
        var jobIndex = this.state.jobIndex;
        var job = jobs[jobIndex];
        var items = job.items;

        var siteAddrLatLng: GoogleAddressItem = {
            lat: job.siteAddressLatitude,
            lng: job.siteAddressLongitude,
            fullAddress: job.siteAddress
        };

        var jobItems = [];
        var scheduleIds: number[] = [];
        for (var i = 0; i < items.length; i++) {
            scheduleIds.push(items[i].id);
            let jobItem = {
                scheduleId: items[i].id,
                kwikstageId: items[i].kwikstageId,
                complete: items[i].isComplete,
                percentComplete: items[i].percentComplete,
                estimatedTimeMins: items[i].estimatedTimeMins,
                itemParts: items[i].itemParts,
                other: items[i].other
            };
            jobItems.push(jobItem);
        }

        //check if there is another job after this one as need to update departure time
        var duration = 0;
        var nextscheduleIds: number[] = [];
        var nextIndex = jobIndex + 1;
        if (nextIndex < jobs.length) {
            var nextJob = jobs[nextIndex];
            if (nextJob) {
                var nextJobItems = nextJob.items;
                for (var i = 0; i < nextJobItems.length; i++) {
                    nextscheduleIds.push(nextJobItems[i].id);
                }

                var nextJobAddrLatLng: GoogleAddressItem = {
                    lat: nextJob.siteAddressLatitude,
                    lng: nextJob.siteAddressLongitude,
                    fullAddress: nextJob.siteAddress
                };

                //calculate distance to job via google
                var result = await UtilsGoogleMaps.calculateDistance(siteAddrLatLng, nextJobAddrLatLng);
                if (result) {
                    duration = result.duration;
                };
            }
        }
        var estTravelTimeMins = Math.round(duration / 60);
        var truck = this.getTruckName(truckJob.truckId);
        var planChanges = this.state.partCompleteJob.planChanges.planChanges * 1;

        var partCompletedSite = {
            scheduleIds: scheduleIds,
            nextScheduleIds: nextscheduleIds,
            estTravelTimeMins: estTravelTimeMins,
            vehicleId: job.truckId,
            jobId: job.jobId,
            siteAddressManual: job.siteAddress,
            siteAddress: job.siteAddress,
            truck: truck,
            jobItems: jobItems,
            completionCheck: this.state.completionCheck,
            notes: this.state.partCompleteJob.notes,
            planChanges: planChanges === 1,
            planComments: this.state.partCompleteJob.planChanges.planComments,
            planNotes: this.state.partCompleteJob.planChanges.notes,
            subId: user.sub,
        };

        var plan = this.state.partCompleteJob.planChanges.plan;
        var partCompleteJob = this.resetPartComplete();
        var completionCheck = this.resetCompletionCheck();

        axios.post('api/Job/PartCompleteJob', partCompletedSite, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            if (res.data.isError) {
                var errors = res.data.messages as any[];
                errors.map(function (error: any) {
                    toast.error(error.content);
                });
            } else {
                if (plan.size > 0) {
                    this.savePlanChanges(token ?? "", user.sub, plan, scheduleIds, nextscheduleIds, job, truckJobList, truckJob, partCompleteJob, completionCheck);
                } else {
                    toast.success("Job has been saved");
                    //update job to complete
                    job.isComplete = true;
                    var allJobsComplete = false;
                    if (nextscheduleIds.length === 0) {
                        allJobsComplete = true;
                    }
                    truckJob.allJobsComplete = allJobsComplete;

                    this.setState({ truckJobList: truckJobList, showDepartSite: false, showPartComplete: false, showPartQuestions: false, showComplete: false, showCompleteQuestions: false, partCompleteJob: partCompleteJob, completionCheck: completionCheck, jobType: 0, isKwikstage: false, siteAddress: "", team: [], saveDisabled: false, isAssistJob: false, isVoidJob: false, isManualJob: false });

                    //re-calculate the overall actual site time
                    this.getActualTimes(truckJob.truckId);
                }
            }
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    //Void Questions - can be called from Part Complete or Complete
    goToVoidQuestions = () => {
        //get the questions for this job.
        var truckJobList = this.state.truckJobList;
        var truckJob = truckJobList[this.state.truckIndex];
        var jobs = truckJob.jobs;
        var job = jobs[this.state.jobIndex];
        var voidJobGearList = job.voidGearList;

        var scheduleIds: number[] = [];
        for (var i = 0; i < job.items.length; i++) {
            scheduleIds.push(job.items[i].id);
        }

        this.setState({ showComplete: false, showVoidQuestions: true, scheduleIds: scheduleIds, voidGearList: voidJobGearList });
    }

    updateVoidGearList = (voidGearList: VoidQuestions, voidDescription: string, index: number) => {
        var voidList = [...this.state.voidGearList];
        var itemToUpdate = voidList[index];
        itemToUpdate.gearList = voidGearList;
        itemToUpdate.voidDescription = voidDescription;

        this.setState({ voidGearList: voidList });
    }

    resetVoidQuestions = () => {
        var newVoidQuestions: VoidQuestions = {
            braces: { ...this.state.voidQuestionsBlank.braces },
            floorEnds: { ...this.state.voidQuestionsBlank.floorEnds },
            plateEnds: { ...this.state.voidQuestionsBlank.plateEnds },
            hookEnds: { ...this.state.voidQuestionsBlank.hookEnds },
            ladders: { ...this.state.voidQuestionsBlank.ladders },
            noLadder: false,
            noLadderReason: "",
            ladderExtras: { ...this.state.voidQuestionsBlank.ladderExtras },
            accessPanels: { ...this.state.voidQuestionsBlank.accessPanels },
            noAccessPanels: false,
            noAccessPanelsReason: "",
            decks700: { ...this.state.voidQuestionsBlank.decks700 },
            decks550: { ...this.state.voidQuestionsBlank.decks550 },
            decks300: { ...this.state.voidQuestionsBlank.decks300 },
            decks150: { ...this.state.voidQuestionsBlank.decks150 },
            noDecks: false,
            noDecksReason: ""
        };

        return newVoidQuestions;
    }

    //Complete Job
    completeJob = () => {
        var completionCheck = this.resetCompletionCheck();
        var completeJob = this.resetCompleteJob();

        var truckJobList = this.state.truckJobList;
        var truckJob = truckJobList[this.state.truckIndex];
        var jobs = truckJob.jobs;
        var job = jobs[this.state.jobIndex];
        var pickupJobs = job.items.filter(i => i.typeId === 2).length;
        var isPickup = false;
        if (job.items.length === pickupJobs) {
            isPickup = true;
        }
        //skip plan changes page if only pickups
        let showComplete = isPickup ? false : true;
        let showCompleteQuestions = isPickup ? true : false;

        this.setState({ showDepartSite: false, showPartComplete: false, showPartQuestions: false, showComplete: showComplete, showCompleteQuestions: showCompleteQuestions, completeJob: completeJob, completionCheck: completionCheck, jobType: job.jobType, isKwikstage: job.isKwikstage, siteAddress: job.siteAddress, team: job.team, isPickup: isPickup });
    }

    resetCompleteJob = () => {
        var completeJob: PlanChanges = {
            planChanges: -1,
            plan: new File([], ""),
            planComments: "",
            notes: ""
        };

        return completeJob;
    }

    completeJobBack = () => {
        this.setState({ showDepartSite: true, showComplete: false, showCompleteQuestions: false, isPickup: false });
    }

    updateComplete = (completeJob: PlanChanges) => {
        this.setState({ completeJob: completeJob });
    }

    gotoCompleteQuestions = () => { 
        this.setState({ showComplete: false, showVoidQuestions: false, showCompleteQuestions: true });
    }

    resetCompletionCheck = () => {
        var completionCheck: CompletionCheck = {
            swmsSiteContact: -1,
            scaffoldSquare: -1,
            scaffoldGaps: -1,
            accessPointsInstalled: -1,
            scaffoldSupported: -1,
            safetySigns: -1,
            handrailsChecked: -1,
            gapsChecked: -1,
            fittingsSecure: -1,
            siteChecked: -1,
            rubbishInBin: -1,
            siteSecured: -1,
            scaffoldSafeCondition: -1,
            scaffoldDamage: -1,
            missingEquipment: -1,
            scaffoldCertificateSigned: -1,
            scaffoldTicketPhoto: -1,
            scaffoldPhotosComplete: -1,
            scaffoldTicketSignedById: -1,
            notes: ""
        };

        return completionCheck;
    }

    completeVoidQuestionsJobBack = () => {
        this.setState({ showDepartSite: false, showComplete: true, showVoidQuestions: false, showCompleteQuestions: false });
        if (this.state.isPickup) {
            //can't have plan changes for pickups so skip completion screen
            this.completeJobBack();
        }
    }

    completeQuestionsJobBack = () => {
        //if (this.state.isVoidJob) {
        //    //need to go back to void questions
        //    this.setState({ showDepartSite: false, showComplete: false, showVoidQuestions: true, showCompleteQuestions: false });
        //} else {
            this.setState({ showDepartSite: false, showComplete: true, showCompleteQuestions: false });
            if (this.state.isPickup) {
                //can't have plan changes for pickups so skip completion screen
                this.completeJobBack();
            }
        //}
    }

    updateCompleteQuestions = (completionCheck: CompletionCheck) => {
        this.setState({ completionCheck: completionCheck });
    }

    saveCompleteSite = () => {
        if (this.state.saveDisabled) {
            return;
        }
        this.setState({ saveDisabled: true });
        this.completeSiteSave();
    }

    async completeSiteSave() {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        var truckJobList = this.state.truckJobList;
        var truckJob = truckJobList[this.state.truckIndex];
        var jobs = truckJob.jobs;
        var jobIndex = this.state.jobIndex;
        var job = jobs[jobIndex];
        var items = job.items;

        var siteAddrLatLng: GoogleAddressItem = {
            lat: job.siteAddressLatitude,
            lng: job.siteAddressLongitude,
            fullAddress: job.siteAddress
        };

        var scheduleIds: number[] = [];
        var voidScheduleIds: number[] = [];     //need to save gear list for void items
        for (var i = 0; i < items.length; i++) {
            scheduleIds.push(items[i].id);
            if (items[i].isVoid) {
                voidScheduleIds.push(items[i].id);
            }
        }

        //check if there is another job after this one as need to update departure time
        var duration = 0;
        var nextscheduleIds: number[] = [];
        var nextIndex = jobIndex + 1;
        if (nextIndex < jobs.length) {
            var nextJob = jobs[nextIndex];
            if (nextJob) {
                var nextJobItems = nextJob.items;
                for (var i = 0; i < nextJobItems.length; i++) {
                    nextscheduleIds.push(nextJobItems[i].id);
                }

                var nextJobAddrLatLng: GoogleAddressItem = {
                    lat: nextJob.siteAddressLatitude,
                    lng: nextJob.siteAddressLongitude,
                    fullAddress: nextJob.siteAddress
                };

                //calculate distance to job via google
                var result = await UtilsGoogleMaps.calculateDistance(siteAddrLatLng, nextJobAddrLatLng);
                if (result) {
                    duration = result.duration;
                };
            } 
        }
        var estTravelTimeMins = Math.round(duration / 60);

        var planChanges = this.state.completeJob.planChanges * 1;
        var truck = this.getTruckName(truckJob.truckId);
        var completedSite = {
            scheduleIds: scheduleIds,
            voidScheduleIds: voidScheduleIds,
            nextScheduleIds: nextscheduleIds,
            estTravelTimeMins: estTravelTimeMins,
            vehicleId: job.truckId,
            jobId: job.jobId,
            siteAddressManual: job.siteAddress,
            siteAddress: job.siteAddress,
            truck: truck,
            completionCheck: this.state.completionCheck,
            planChanges: planChanges === 1,
            planComments: this.state.completeJob.planComments,
            voidGearList: this.state.voidGearList,
            notes: this.state.completeJob.notes,
            subId: user.sub,
        };

        var partCompleteJob = this.resetPartComplete();
        var completionCheck = this.resetCompletionCheck();

        var plan = this.state.completeJob.plan;
        
        axios.post('api/Job/CompleteSite', completedSite, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            if (res.data.isError) {
                var errors = res.data.messages as any[];
                errors.map(function (error: any) {
                    toast.error(error.content);
                });
            } else {
                if (plan.size > 0) {
                    this.savePlanChanges(token ?? "", user.sub, plan, scheduleIds, nextscheduleIds, job, truckJobList, truckJob, partCompleteJob, completionCheck);
                } else {
                    toast.success("Job has been saved.");
                    //update job to complete
                    job.isComplete = true;
                    var allJobsComplete = false;
                    if (nextscheduleIds.length === 0) {
                        allJobsComplete = true;
                    }
                    truckJob.allJobsComplete = allJobsComplete;

                    this.setState({ truckJobList: truckJobList, showDepartSite: false, showPartComplete: false, showPartQuestions: false, showComplete: false, showCompleteQuestions: false, completionCheck: completionCheck, jobType: 0, isKwikstage: false, siteAddress: "", team: [], voidGearList: [], saveDisabled: false, isAssistJob: false, isVoidJob: false, isManualJob: false });

                    //re-calculate the overall actual site time
                    this.getActualTimes(truckJob.truckId);
                }
            }
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    savePlanChanges = (token: string, subId: string, plan: File, scheduleIds: number[], nextscheduleIds: number[], job: DriverJob, truckJobList: TruckJobList[], truckJob: TruckJobList, partCompleteJob: PartCompleteJob, completionCheck: CompletionCheck) => {
        //upload the file
        const data = new FormData()
        data.append('ScheduleId', scheduleIds[0].toString());
        data.append('TypeId', "1");
        data.append('subId', subId);
        data.append('photo', plan);

        //save photos
        axios.post('api/Job/SaveSchedulePhoto', data, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            if (res.data.isError) {
                var errors = res.data.messages as any[];
                errors.map(function (error: any) {
                    toast.error(error.content);
                });
            } else {
                toast.success("Job has been saved.");
                //update job to complete
                job.isComplete = true;
                var allJobsComplete = false;
                if (nextscheduleIds.length === 0) {
                    allJobsComplete = true;
                }
                truckJob.allJobsComplete = allJobsComplete;

                this.setState({ truckJobList: truckJobList, showDepartSite: false, showPartComplete: false, showPartQuestions: false, showComplete: false, showCompleteQuestions: false, partCompleteJob: partCompleteJob, completionCheck: completionCheck, jobType: 0, isKwikstage: false, siteAddress: "", team: [], voidGearList: [], saveDisabled: false, isAssistJob: false, isVoidJob: false, isManualJob: false });
                //re-calculate the overall actual site time
                this.getActualTimes(truckJob.truckId);
            }
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    //complete tool check from last job
    goToolCheck = () => {
        var truckJobList = this.state.truckJobList;
        var truckJob = truckJobList[this.state.truckIndex];

        this.props.history.push("/drivertoolcheck/" + truckJob.truckId + "/2");
    }

    //Lunch Break
    saveLunch = (scheduleId: number, jobId: number) => {
        this.saveLunchBreak(scheduleId, jobId);
    }

    async saveLunchBreak(scheduleId: number, jobId: number) {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        var truckJobList = this.state.truckJobList;
        var truckJob = truckJobList[this.state.truckIndex];

        var lunchBreak = {
            truckId: truckJob.truckId,
            scheduleId: scheduleId,
            jobId: jobId,
            subId: user.sub,
        };

        axios.post('api/Job/SaveLunchBreak', lunchBreak, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            if (res.data.isError) {
                var errors = res.data.messages as any[];
                errors.map(function (error: any) {
                    toast.error(error.content);
                });
            } else {
                toast.success("Lunch Break has been saved.");
                truckJob.hadLunch = true;

                this.setState({ truckJobList: truckJobList, saveDisabled: false });
            }
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    render() {
        let contents = this.state.loading
            ? <p><em>Loading...</em></p>
            : this.renderDetails();

        return <div>
            {contents}
        </div>;
    }

    renderDetails = () => {
        var truckId = this.state.truckJobList.length > 0 && this.state.truckIndex >= 0 ? this.state.truckJobList[this.state.truckIndex].truckId : -1;
        var driverTabsMobile = <DriverTabData tab="driverjobs" truckId={truckId} />

        var contents;
        if (this.state.showDepartOffice) {
            contents = <DriverJobsDepartOfficeData truckId={truckId} departureCheck={this.state.departureCheck} saveDisabled={this.state.saveDisabled} updateDetails={this.updateDepartureCheck} save={this.saveDepartOffice} />
        } else if (this.state.showArriveSite) {
            contents = <DriverJobsSiteArrivalData truckId={truckId} arrivalCheck={this.state.arrivalCheck} back={this.backtoJobs} updateDetails={this.updateArrivalCheck} save={this.gotoSwms} />
        } else if (this.state.showArriveSiteSwms) {
            contents = <DriverJobsSiteArrivalSwmsData scheduleIds={this.state.scheduleIds} swmsOptions={this.state.swmsOptions} swmsManualItems={this.state.swmsManualItems} swmsChecked={this.state.arrivalCheck.siteSwmsChecked} vehicleId={truckId} saveDisabled={this.state.saveDisabled} back={this.backtoArrivalChecks} update={this.updateSwmsChecked} updateManualSwms={this.updateManualSwms} save={this.saveArriveSite} />
        } else if (this.state.showDepartSite) {
            contents = <DriverJobsSiteDepartureData isAssistJob={this.state.isAssistJob} isManualJob={this.state.isManualJob} cancel={this.cancelDepartSite} skipJob={this.skipJob} partCompleteJob={this.partCompleteJob} completeJob={this.completeJob} />
        } else if (this.state.showSkipJob) {
            contents = <DriverJobsSiteDepartureSkipData saveDisabled={this.state.saveDisabled} back={this.skipJobBack} notes={this.state.skipJobNotes} update={this.updateSkipJobNotes} save={this.saveSkipJob} />
        } else if (this.state.showPartComplete) {
            contents = <DriverJobsSiteDeparturePartData back={this.partCompleteJobBack} partCompleteJob={this.state.partCompleteJob} update={this.updatePartComplete} next={this.goToPartItems} />
        } else if (this.state.showPartItems) {
            contents = <DriverJobsSiteDeparturePartItemsData back={this.partItemsBack} partCompleteJob={this.state.partCompleteJob} update={this.updatePartComplete} next={this.gotoPartQuestions} />
        } else if (this.state.showPartQuestions) {
            contents = <DriverJobsSiteDeparturePartQuestionsData saveDisabled={this.state.saveDisabled} back={this.partQuestionsBack} team={this.state.team} completionCheck={this.state.completionCheck} jobType={this.state.jobType} isKwikstage={this.state.isKwikstage} update={this.updatePartQuestions} save={this.savePartComplete} />
        } else if (this.state.showComplete) {
            contents = <DriverJobsSiteDepartureCompleteData back={this.completeJobBack} completeJob={this.state.completeJob} isPickup={this.state.isPickup} update={this.updateComplete} next={this.gotoCompleteQuestions} />
        //    contents = <DriverJobsSiteDepartureCompleteData back={this.completeJobBack} completeJob={this.state.completeJob} isPickup={this.state.isPickup} update={this.updateComplete} next={this.state.isVoidJob ? this.goToVoidQuestions : this.gotoCompleteQuestions} />
        //} else if (this.state.showVoidQuestions) {
        //    contents = <DriverJobsSiteDepartureVoidQuestionsData back={this.completeVoidQuestionsJobBack} fromCompleteJob={true} scheduleIds={this.state.scheduleIds} voidGearList={this.state.voidGearList} update={this.updateVoidGearList} next={this.gotoCompleteQuestions } />
        } else if (this.state.showCompleteQuestions) {
            contents = <DriverJobsSiteDepartureCompleteQuestionsData saveDisabled={this.state.saveDisabled} back={this.completeQuestionsJobBack} team={this.state.team} completionCheck={this.state.completionCheck} jobType={this.state.jobType} isKwikstage={this.state.isKwikstage} update={this.updateCompleteQuestions} save={this.saveCompleteSite} />
        } else {
            contents = <DriverJobsListData truckIndex={this.state.truckIndex} truckId={truckId} truckOptions={this.state.truckOptions} truckJobList={this.state.truckJobList} scheduleTypes={this.state.scheduleTypes} updateTruckIndex={this.updateTruckIndex} departOffice={this.departOffice} arriveOffice={this.arriveOffice} arriveSite={this.startJob} departSite={this.departSite} showSiteIssues={this.showSiteIssues} goToolCheck={this.goToolCheck} saveLunch={this.saveLunch} refreshJobs={this.refreshJobs} updateJobs={this.updateJobs} />
        }

        return (
            <div>
                <div>
                    
                </div>
                <div className="mobileFont marginTop70 marginBottom70">
                    {contents}
                </div>
                <div className={this.state.showArriveSite ? "hidden" : ""}>
                    {driverTabsMobile}
                </div>
            </div>
        );

    }
}
